import type { LegalDocument } from "../lib/legal";

/** Anchor id for a legal section heading, e.g. "4. Data Security" -> "data-security". */
function slug(heading: string) {
  return heading
    .replace(/^\d+\.\s*/, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

/**
 * Renders a Privacy Policy / Terms document: table of contents plus sections.
 * Used by both legal pages so they stay consistent.
 */
export default function LegalArticle({ doc }: { doc: LegalDocument }) {
  return (
    <div className="container-page section-pad">
      <div className="grid gap-12 lg:grid-cols-[260px_1fr]">
        <nav aria-label="Contents" className="lg:sticky lg:top-24 lg:self-start">
          <h2 className="text-xs font-semibold uppercase tracking-[0.16em] text-ink-muted">
            Contents
          </h2>
          <ul className="mt-4 space-y-2 border-l border-ink-line pl-4">
            {doc.sections.map((section) => (
              <li key={section.heading}>
                <a
                  href={`#${slug(section.heading)}`}
                  className="text-sm text-ink-muted transition-colors hover:text-brand-600"
                >
                  {section.heading}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        <article className="max-w-3xl">
          <p className="text-sm font-medium text-ink-muted">
            Last updated: <time>{doc.updated}</time>
          </p>
          <p className="mt-6 text-base leading-relaxed text-ink">{doc.intro}</p>

          <div className="mt-12 space-y-12">
            {doc.sections.map((section, index) => {
              const isLast = index === doc.sections.length - 1;

              return (
                <section key={section.heading} id={slug(section.heading)}>
                  <h2 className="text-lg font-bold tracking-tight text-ink">
                    {section.heading}
                  </h2>

                  <div className="mt-4 space-y-4">
                    {section.blocks.map((block, blockIndex) =>
                      block.type === "p" ? (
                        <p key={blockIndex} className="text-sm leading-relaxed text-ink-muted">
                          {block.text}
                        </p>
                      ) : (
                        <ul key={blockIndex} className="space-y-2.5 pl-1">
                          {block.items.map((item) => (
                            <li
                              key={item}
                              className="flex gap-3 text-sm leading-relaxed text-ink-muted"
                            >
                              <span
                                aria-hidden="true"
                                className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-300"
                              />
                              {item}
                            </li>
                          ))}
                        </ul>
                      )
                    )}
                  </div>

                  {/* The final section is the contact block, so render the details there. */}
                  {isLast && (
                    <dl className="mt-6 space-y-3 rounded-2xl border border-ink-line bg-ink-soft p-6">
                      {doc.contact.map((row) => (
                        <div key={row.label} className="flex flex-col gap-0.5 sm:flex-row sm:gap-4">
                          <dt className="w-24 shrink-0 text-xs font-semibold uppercase tracking-wide text-ink-muted">
                            {row.label}
                          </dt>
                          <dd className="text-sm text-ink">
                            {/^https?:\/\//.test(row.value) || row.value.includes("@") ? (
                              <a
                                href={
                                  row.value.includes("@")
                                    ? `mailto:${row.value}`
                                    : row.value
                                }
                                className="font-semibold text-brand-600 hover:text-brand-700"
                              >
                                {row.value}
                              </a>
                            ) : (
                              row.value
                            )}
                          </dd>
                        </div>
                      ))}
                    </dl>
                  )}
                </section>
              );
            })}
          </div>

          {doc.contactOutro && (
            <p className="mt-8 text-sm leading-relaxed text-ink-muted">{doc.contactOutro}</p>
          )}
        </article>
      </div>
    </div>
  );
}
